import { useMemo } from 'react';

import { Checkbox, HStack } from '@rtkwlf/fenrir-react';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useEnhancedScannerDetails } from '../../../../../../../../../../../hooks/use-enhanced-scanner-details/use-enhanced-scanner-details';
import type { ScanExclusionType } from '../../add-ip-range-form/add-ip-range-form';
import { DeleteExclusionDialog } from '../../delete-exclusion-dialog/delete-exclusion-dialog';
import { EditExclusionDialog } from '../../edit-exclusion-dialog/edit-exclusion-dialog';

export const useScanExclusionColumns = () => {
  const { t } = useTranslation('telemetryScannerConfig');
  const columnHelper = createColumnHelper<ScanExclusionType>();

  const { scannerId = '' } = useParams();

  const { data: scannerDetails } = useEnhancedScannerDetails(scannerId, {
    select: (d) => ({
      hasPortExemptionCapability: d.capabilities.includes(
        'port_scan_exemption',
      ),
    }),
  });

  return useMemo(() => {
    const columns = [
      columnHelper.display({
        cell: ({ row }) => (
          <Checkbox.Root
            isChecked={row.getIsSelected()}
            isIndeterminate={row.getIsSomeSelected()}
            onCheckedChange={row.getToggleSelectedHandler()}
          />
        ),
        header: ({ table }) => (
          <Checkbox.Root
            isChecked={table.getIsAllRowsSelected()}
            isIndeterminate={table.getIsSomeRowsSelected()}
            onCheckedChange={table.toggleAllRowsSelected}
          />
        ),
        id: 'select',
        meta: {
          width: '5%',
        },
      }),
      columnHelper.accessor('ipRange', {
        cell: (info) => info.getValue(),
        enableSorting: true,
        header: t('scanExclusion.table.ipAddressRange'),
        id: 'ipRange',
        minSize: 50,
      }),
      columnHelper.accessor('ports', {
        cell: (info) => info.getValue(),
        enableSorting: true,
        header: t('scanExclusion.table.ports'),
        id: 'ports',
        minSize: 20,
      }),
      columnHelper.display({
        cell: ({ row: { index, original } }) => {
          return (
            <HStack data-testid="sticky-column" gap="xsmall">
              <EditExclusionDialog index={index} original={original} />
              <DeleteExclusionDialog index={index} />
            </HStack>
          );
        },
        header: undefined,
        id: 'actions',
      }),
    ];

    if (scannerDetails?.isConnected === false) {
      return columns.filter(
        (col) =>
          col.id !== 'ports' && col.id !== 'actions' && col.id !== 'select',
      );
    }

    return scannerDetails?.hasPortExemptionCapability
      ? columns
      : columns.filter(({ id }) => id !== 'ports');
  }, [columnHelper, scannerDetails, t]);
};
